import React, { useContext, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import {
  shopImageUpload,
  barberImageUpload,
} from '../../middleware/registration_service/updateRequest';
import {
  barberDeleteImage,
  shopDeleteImage,
} from '../../middleware/registration_service/deleteRequest';
import { roleContext } from '../../context/RoleContext';
import { GalleryImageName } from '../../types/barber';
import { AxiosResponse } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfileData } from '../../context/ProfileDataContext';

const MAX_GALLERY_IMAGES = 20;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function GalleryImageList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const role = useContext(roleContext);
  const { shopId, barberId, shop, barber, setShop, setBarber, loading } =
    useProfileData();
  const { getAccessTokenSilently } = useAuth0();

  const [activeIndex, setActiveIndex] = useState(0);

  // delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<GalleryImageName | null>(
    null
  );

  // lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // snackbar
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSnack = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => setSnack({ open: true, message, severity });

  // touch swipe
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const images: GalleryImageName[] =
    role === 'Shop Owner'
      ? shop?.shopGalleryImageName ?? []
      : barber?.galleryImageName ?? [];

  const total = images.length;

  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + total) % total),
    [total]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % total),
    [total]
  );

  // clamp index when images change
  React.useEffect(() => {
    if (total > 0 && activeIndex >= total) setActiveIndex(total - 1);
  }, [total, activeIndex]);

  const selectImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      showSnack('Please select a file.', 'error');
      return;
    }
    if (total >= MAX_GALLERY_IMAGES) {
      showSnack(
        `Gallery upload limit reached. You can upload up to ${MAX_GALLERY_IMAGES} images.`,
        'error'
      );
      e.target.value = '';
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      if (role === 'Shop Owner') {
        const r = await shopImageUpload(file, shopId, token);
        setShop(r.data);
      } else if (role === 'Barber') {
        const r = await barberImageUpload(file, barberId, token);
        setBarber(r.data);
      }
      showSnack('Image uploaded successfully.');
    } catch (err: any) {
      showSnack(err?.response?.data ?? 'Upload failed.', 'error');
    }
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete) return;
    const { id, imageName } = imageToDelete as GalleryImageName;
    try {
      setDeleting(true);
      const token = await getAccessTokenSilently();
      let response: AxiosResponse;
      if (role === 'Shop Owner' && shop?.id) {
        response = await shopDeleteImage(imageName, id, shop.id, token);
        if (response?.status === 200) {
          setShop((p) =>
            p
              ? {
                  ...p,
                  shopGalleryImageName: p.shopGalleryImageName?.filter(
                    (x) => x.id !== id
                  ),
                }
              : p
          );
        }
      } else if (role === 'Barber' && barber?.id) {
        response = await barberDeleteImage(imageName, id, barber.id, token);
        if (response?.status === 200) {
          setBarber((p) =>
            p
              ? {
                  ...p,
                  galleryImageName: p.galleryImageName?.filter(
                    (x) => x.id !== id
                  ),
                }
              : p
          );
        }
      }
      showSnack('Image deleted successfully.');
    } catch (err: any) {
      showSnack(
        err?.response?.data ?? err?.message ?? 'Delete failed.',
        'error'
      );
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #eee',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#fafafa',
      }}
    >
      {/* ── Header ── */}
      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', bgcolor: '#fff' }}
      >
        <Grid item>
          <Button
            component="label"
            role={undefined}
            tabIndex={-1}
            disabled={total >= MAX_GALLERY_IMAGES}
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            sx={{
              bgcolor: '#dd8709',
              color: '#fff',
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { bgcolor: '#b86e08' },
              '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
            }}
          >
            Add Image ({total}/{MAX_GALLERY_IMAGES})
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={selectImage}
              disabled={total >= MAX_GALLERY_IMAGES}
            />
          </Button>
        </Grid>
      </Grid>

      {/* ── Carousel ── */}
      {loading || deleting ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : total === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 260,
            color: '#999',
            gap: 1,
          }}
        >
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 56, color: '#ccc' }} />
          <Typography variant="body2">
            No images yet. Click "Add Image" to get started.
          </Typography>
        </Box>
      ) : (
        <>
          {/* main slide */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: isMobile ? 260 : 420,
              bgcolor: '#111',
              overflow: 'hidden',
            }}
            onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchStartX === null) return;
              const diff = touchStartX - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
              setTouchStartX(null);
            }}
          >
            <Box
              component="img"
              src={images[activeIndex]?.imageSelfLink}
              alt={`Gallery image ${activeIndex + 1}`}
              onClick={() => setLightboxOpen(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'zoom-in',
                transition: 'opacity 0.3s',
                display: 'block',
              }}
            />

            {/* overlay gradient */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 80,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
              }}
            />

            {/* prev / next arrows */}
            {total > 1 && (
              <>
                <IconButton
                  onClick={prev}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={next}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {/* delete button */}
            <IconButton
              onClick={() => {
                setImageToDelete(images[activeIndex] as GalleryImageName);
                setDeleteDialogOpen(true);
              }}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: '#ff6b6b',
                '&:hover': { bgcolor: 'rgba(180,0,0,0.7)', color: '#fff' },
              }}
              aria-label="Delete image"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>

            {/* counter */}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 12,
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              {activeIndex + 1} / {total}
            </Typography>
          </Box>

          {/* ── Dot indicators ── */}
          {total > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 0.75,
                py: 1.5,
              }}
            >
              {images.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  sx={{
                    width: i === activeIndex ? 20 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: i === activeIndex ? '#2b2b33' : '#ccc',
                    cursor: 'pointer',
                    transition: 'all 0.25s',
                  }}
                />
              ))}
            </Box>
          )}

          {/* ── Thumbnail strip ── */}
          {total > 1 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 2,
                pb: 2,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#ccc',
                  borderRadius: 2,
                },
              }}
            >
              {images.map((img, i) => (
                <Box
                  key={img.id as any}
                  onClick={() => setActiveIndex(i)}
                  component="img"
                  src={img.imageSelfLink}
                  alt={`thumb ${i + 1}`}
                  sx={{
                    width: 72,
                    height: 56,
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    flexShrink: 0,
                    outline:
                      i === activeIndex
                        ? '2px solid #2b2b33'
                        : '2px solid transparent',
                    opacity: i === activeIndex ? 1 : 0.6,
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {/* ── Delete confirmation dialog ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}>
          Delete image?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove the image. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              borderColor: '#333',
              color: '#333',
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteImage}
            sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {images[activeIndex] && (
            <Box
              component="img"
              src={images[activeIndex].imageSelfLink}
              alt="Full size"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                maxHeight: '80vh',
              }}
            />
          )}
          {total > 1 && (
            <>
              <IconButton
                onClick={prev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton
                onClick={next}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
